/**
 * Quick-action command source for the universal command registry (#1514).
 *
 * "Do" commands — create a record, toggle the theme, jump to a self-service
 * page. ``run()`` performs the action via INJECTED handlers (navigate / theme
 * toggle) so the builder stays pure + unit-testable (no React, no router, no
 * DOM dependency).
 *
 * #1536: the four CREATE actions navigate to their list page WITH the one-shot
 * ``?new=1`` trigger param. Each list page reads that param on mount, opens its
 * create modal, then strips the param so a refresh/back never re-opens it — the
 * action used to just land on the list (a local button onClick opened the
 * modal), so it "looked like it did nothing".
 *
 * Gating mirrors the nav's own-book / listing rules so an action never appears
 * where its target page would be hidden:
 *  - Create contact / New buyer: own-book → hidden for back-office personas,
 *    unless the user still works their own book (D2-USR-010, ``hasOwnBook``).
 *  - Add a listing: own-book BUT also visible to a ``list_properties`` holder
 *    (the office-admin lists on behalf of the agency — mirrors ``listingCapable``).
 *  - New task / Toggle theme / Connect your AI / Personal settings: all users.
 */
import {
  UserPlus,
  Building2,
  CheckSquare,
  UserSearch,
  SunMoon,
  Plug,
  UserCog,
} from "lucide-react";
import type {
  ActionCommand,
  CommandActionHandlers,
  CommandContext,
} from "./types";
import { worksOwnBook } from "../../components/layout/crmNav";

/**
 * Build the filtered, RBAC-gated quick-action command list. ``handlers`` carries
 * the side effects (navigate / toggleTheme); kept injectable for testing.
 */
export function buildActionCommands(
  ctx: CommandContext,
  handlers: CommandActionHandlers,
): ActionCommand[] {
  const { navigate, toggleTheme } = handlers;
  const out: ActionCommand[] = [];
  // Mirrors the nav's agentBookOnly rule, incl. the D2-USR-010 own-book carve-out.
  const ownBookVisible = !ctx.isBackOffice || worksOwnBook(ctx);

  // Create new contact — own-book; hidden for back-office (mirrors agentBookOnly).
  if (ownBookVisible) {
    out.push({
      id: "action:create-contact",
      kind: "action",
      label: "Create new contact",
      sublabel: "Add a person to your contacts",
      icon: UserPlus,
      run: () => navigate("/crm/contacts?new=1"),
    });
  }

  // Add a listing — own-book OR a list_properties holder (mirrors listingCapable).
  if (ownBookVisible || ctx.has("list_properties")) {
    out.push({
      id: "action:add-listing",
      kind: "action",
      label: "Add a listing",
      sublabel: "List a property",
      icon: Building2,
      run: () => navigate("/crm/listings?new=1"),
    });
  }

  // New task — Tasks has no nav gate; all authenticated users.
  out.push({
    id: "action:new-task",
    kind: "action",
    label: "New task",
    sublabel: "Add a to-do",
    icon: CheckSquare,
    run: () => navigate("/crm/tasks?new=1"),
  });

  // New buyer / lead — own-book; hidden for back-office.
  if (ownBookVisible) {
    out.push({
      id: "action:new-buyer",
      kind: "action",
      label: "New buyer/lead",
      sublabel: "Add a buyer requirement",
      icon: UserSearch,
      run: () => navigate("/crm/buyers?new=1"),
    });
  }

  // Toggle dark/light mode — all users.
  out.push({
    id: "action:toggle-theme",
    kind: "action",
    label: "Toggle dark/light mode",
    icon: SunMoon,
    run: () => toggleTheme(),
  });

  // Connect your AI — admins land on the admin connect page, everyone else the
  // CRM connect page (both routes exist in their respective shells).
  out.push({
    id: "action:connect-ai",
    kind: "action",
    label: "Connect your AI",
    sublabel: "Set up the MCP connection",
    icon: Plug,
    run: () => navigate(ctx.role === "admin" ? "/admin/connect" : "/crm/connect"),
  });

  // Open Personal settings — all authenticated users.
  out.push({
    id: "action:personal-settings",
    kind: "action",
    label: "Open Personal settings",
    sublabel: "Edit your profile and password",
    icon: UserCog,
    run: () => navigate("/crm/settings"),
  });

  return out;
}
